/**
 * Color conversion utilities for the theme system.
 * Converts hex colors to HSL triplet strings matching globals.css format.
 */

/**
 * Convert hex color (#RRGGBB or #RGB) to HSL triplet string "H S% L%"
 * matching the bare-value format used in globals.css `:root` variables.
 */
export function hexToHslString(hex: string): string {
  const { h, s, l } = hexToHsl(hex);
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
}

/** Convert an {r,g,b} channel (0-255) back to a #RRGGBB hex string. */
function rgbToHex(r: number, g: number, b: number): string {
  const to2 = (x: number) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/**
 * Linearly blend hex color `a` towards hex color `b` by `t` (0 = all a, 1 = all
 * b), returning the result as an HSL triplet string. Used to nudge a theme's
 * recessed surface a little lighter (subAlt → bg) without leaving its palette.
 */
export function mixHexToHslString(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const mix = (x: number, y: number) => x + (y - x) * t;
  return hexToHslString(rgbToHex(mix(ca.r, cb.r), mix(ca.g, cb.g), mix(ca.b, cb.b)));
}

/**
 * Determine if a hex color is "light" (luminance > 0.5).
 */
export function isLightColor(hex: string): boolean {
  const { r, g, b } = hexToRgb(hex);
  // Relative luminance (ITU-R BT.709)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

/**
 * Given a hex color, return white or dark hex for readable contrast.
 */
export function contrastForeground(hex: string): string {
  return isLightColor(hex) ? "#000000" : "#ffffff";
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.replace("#", "");
  if (h.length === 3) {
    h = h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2);
  }
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = hexToRgb(hex);
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === rn) {
    h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  } else if (max === gn) {
    h = ((bn - rn) / d + 2) * 60;
  } else {
    h = ((rn - gn) / d + 4) * 60;
  }

  return { h, s: s * 100, l: l * 100 };
}
